import { type IntlShape } from 'react-intl';
import { UserTasteTraitLevel } from '~/libs/gql/types';

// What each trait is about, for every feature User::TasteProfile::TraitsService::FEATURES can return, or null for a
// feature this version of the page has no name for
const traitSubject = (intl: IntlShape, feature: string) => {
  switch (feature) {
    case 'adventurousness':
      return intl.formatMessage({ defaultMessage: 'adventurous dishes' });
    case 'breakfast':
      return intl.formatMessage({ defaultMessage: 'breakfast food' });
    case 'cuisine_region.caribbean':
      return intl.formatMessage({ defaultMessage: 'Caribbean food' });
    case 'cuisine_region.east_asian':
      return intl.formatMessage({ defaultMessage: 'East Asian food' });
    case 'cuisine_region.latin_american':
      return intl.formatMessage({ defaultMessage: 'Latin American food' });
    case 'cuisine_region.middle_eastern_north_african':
      return intl.formatMessage({ defaultMessage: 'Middle Eastern and North African food' });
    case 'cuisine_region.north_american':
      return intl.formatMessage({ defaultMessage: 'American food' });
    case 'cuisine_region.northern_european':
      return intl.formatMessage({ defaultMessage: 'Northern European food' });
    case 'cuisine_region.south_asian':
      return intl.formatMessage({ defaultMessage: 'South Asian food' });
    case 'cuisine_region.southeast_asian':
      return intl.formatMessage({ defaultMessage: 'Southeast Asian food' });
    case 'cuisine_region.southern_european':
      return intl.formatMessage({ defaultMessage: 'Southern European food' });
    case 'cuisine_region.sub_saharan_african':
      return intl.formatMessage({ defaultMessage: 'African food' });
    case 'dish_form_group.bakery_sweets':
      return intl.formatMessage({ defaultMessage: 'pancakes, pastries and desserts' });
    case 'dish_form_group.handhelds':
      return intl.formatMessage({ defaultMessage: 'burgers, sandwiches and tacos' });
    case 'dish_form_group.noodles_pasta':
      return intl.formatMessage({ defaultMessage: 'noodles and pasta' });
    case 'dish_form_group.rice_bowls':
      return intl.formatMessage({ defaultMessage: 'rice dishes and bowls' });
    case 'dish_form_group.shareables':
      return intl.formatMessage({ defaultMessage: 'wings, dumplings and small plates' });
    case 'dish_form_group.soups_curries':
      return intl.formatMessage({ defaultMessage: 'soups and curries' });
    case 'flavor.bitter':
      return intl.formatMessage({ defaultMessage: 'bitter flavors' });
    case 'flavor.cheesy':
      return intl.formatMessage({ defaultMessage: 'cheesy dishes' });
    case 'flavor.funky':
      return intl.formatMessage({ defaultMessage: 'funky, fermented flavors' });
    case 'flavor.garlicky':
      return intl.formatMessage({ defaultMessage: 'garlicky, punchy flavors' });
    case 'flavor.herby':
      return intl.formatMessage({ defaultMessage: 'fresh herbs' });
    case 'flavor.smoky':
      return intl.formatMessage({ defaultMessage: 'smoky flavors' });
    case 'flavor.sweet':
      return intl.formatMessage({ defaultMessage: 'sweet flavors' });
    case 'flavor.tangy':
      return intl.formatMessage({ defaultMessage: 'tangy flavors' });
    case 'flavor.umami':
      return intl.formatMessage({ defaultMessage: 'rich, savory flavors' });
    case 'flavor.warm_spiced':
      return intl.formatMessage({ defaultMessage: 'warm spices' });
    case 'heaviness':
      return intl.formatMessage({ defaultMessage: 'hearty dishes' });
    case 'ingredients.anchovy':
      return intl.formatMessage({ defaultMessage: 'anchovies' });
    case 'ingredients.avocado':
      return intl.formatMessage({ defaultMessage: 'avocado' });
    case 'ingredients.blue_cheese':
      return intl.formatMessage({ defaultMessage: 'blue cheese' });
    case 'ingredients.cilantro':
      return intl.formatMessage({ defaultMessage: 'cilantro' });
    case 'ingredients.coconut':
      return intl.formatMessage({ defaultMessage: 'coconut' });
    case 'ingredients.eggplant':
      return intl.formatMessage({ defaultMessage: 'eggplant' });
    case 'ingredients.mayo':
      return intl.formatMessage({ defaultMessage: 'mayo and aioli' });
    case 'ingredients.mushroom':
      return intl.formatMessage({ defaultMessage: 'mushrooms' });
    case 'ingredients.olive':
      return intl.formatMessage({ defaultMessage: 'olives' });
    case 'ingredients.pickles':
      return intl.formatMessage({ defaultMessage: 'pickles' });
    case 'ingredients.raw_onion':
      return intl.formatMessage({ defaultMessage: 'raw onion' });
    case 'method.braised':
      return intl.formatMessage({ defaultMessage: 'braised, slow-cooked dishes' });
    case 'method.fried':
      return intl.formatMessage({ defaultMessage: 'fried food' });
    case 'method.grilled':
      return intl.formatMessage({ defaultMessage: 'grilled dishes' });
    case 'method.raw':
      return intl.formatMessage({ defaultMessage: 'raw and cured dishes like sushi and crudo' });
    case 'method.roasted':
      return intl.formatMessage({ defaultMessage: 'roasted and baked dishes' });
    case 'method.smoked':
      return intl.formatMessage({ defaultMessage: 'smoked dishes' });
    case 'method.steamed':
      return intl.formatMessage({ defaultMessage: 'steamed dishes' });
    case 'method.stir_fried':
      return intl.formatMessage({ defaultMessage: 'stir-fries' });
    case 'protein.beans_lentils':
      return intl.formatMessage({ defaultMessage: 'beans and lentils' });
    case 'protein.beef':
      return intl.formatMessage({ defaultMessage: 'beef' });
    case 'protein.cheese_led':
      return intl.formatMessage({ defaultMessage: 'cheese-centered dishes' });
    case 'protein.chicken':
      return intl.formatMessage({ defaultMessage: 'chicken' });
    case 'protein.egg':
      return intl.formatMessage({ defaultMessage: 'egg dishes' });
    case 'protein.fish':
      return intl.formatMessage({ defaultMessage: 'fish' });
    case 'protein.game':
      return intl.formatMessage({ defaultMessage: 'game meats' });
    case 'protein.lamb_goat':
      return intl.formatMessage({ defaultMessage: 'lamb and goat' });
    case 'protein.other_poultry':
      return intl.formatMessage({ defaultMessage: 'duck and turkey' });
    case 'protein.pork':
      return intl.formatMessage({ defaultMessage: 'pork' });
    case 'protein.shellfish':
      return intl.formatMessage({ defaultMessage: 'shellfish' });
    case 'protein.tofu_tempeh_seitan':
      return intl.formatMessage({ defaultMessage: 'tofu and plant-based proteins' });
    case 'spice':
      return intl.formatMessage({ defaultMessage: 'spicy dishes' });
    case 'texture.brothy':
      return intl.formatMessage({ defaultMessage: 'brothy dishes' });
    case 'texture.chewy':
      return intl.formatMessage({ defaultMessage: 'chewy textures' });
    case 'texture.creamy':
      return intl.formatMessage({ defaultMessage: 'creamy dishes' });
    case 'texture.crispy':
      return intl.formatMessage({ defaultMessage: 'crispy, crunchy dishes' });
    case 'texture.saucy':
      return intl.formatMessage({ defaultMessage: 'saucy dishes' });
    default:
      return null;
  }
};

type TasteTrait = {
  feature: string
  isConfident: boolean
  level: UserTasteTraitLevel
}

// The note Refine your palate shows when a pick teaches us a trait, or null for a feature this version of the page has
// no name for. Softer while the profile is still unsure, like formatTasteTrait.
export const formatTasteNotice = (intl: IntlShape, { feature, isConfident, level }: TasteTrait) => {
  const subject = traitSubject(intl, feature);
  if (!subject) return null;

  if (!isConfident) {
    return level === UserTasteTraitLevel.Loves || level === UserTasteTraitLevel.Leans ?
      intl.formatMessage({ defaultMessage: 'Noted: you might like {subject}' }, { subject }) :
      intl.formatMessage({ defaultMessage: 'Noted: you might pass on {subject}' }, { subject });
  }

  switch (level) {
    case UserTasteTraitLevel.Leans:
      return intl.formatMessage({ defaultMessage: 'Noted: you lean toward {subject}' }, { subject });
    case UserTasteTraitLevel.Loves:
      return intl.formatMessage({ defaultMessage: 'Noted: you love {subject}' }, { subject });
    case UserTasteTraitLevel.RarelyPicks:
      return intl.formatMessage({ defaultMessage: 'Noted: you rarely pick {subject}' }, { subject });
    case UserTasteTraitLevel.SteersClear:
      return intl.formatMessage({ defaultMessage: 'Noted: you steer clear of {subject}' }, { subject });
    default:
      return null;
  }
};

// The trait in plain language, or null for a feature this version of the page has no name for
export const formatTasteTrait = (intl: IntlShape, { feature, isConfident, level }: TasteTrait) => {
  const subject = traitSubject(intl, feature);
  if (!subject) return null;

  // Softer wording while the profile is still unsure, whatever the strength
  if (!isConfident) {
    return level === UserTasteTraitLevel.Loves || level === UserTasteTraitLevel.Leans ?
      intl.formatMessage({ defaultMessage: 'Might like {subject}' }, { subject }) :
      intl.formatMessage({ defaultMessage: 'Might pass on {subject}' }, { subject });
  }

  switch (level) {
    case UserTasteTraitLevel.Leans:
      return intl.formatMessage({ defaultMessage: 'Leans toward {subject}' }, { subject });
    case UserTasteTraitLevel.Loves:
      return intl.formatMessage({ defaultMessage: 'Loves {subject}' }, { subject });
    case UserTasteTraitLevel.RarelyPicks:
      return intl.formatMessage({ defaultMessage: 'Rarely picks {subject}' }, { subject });
    case UserTasteTraitLevel.SteersClear:
      return intl.formatMessage({ defaultMessage: 'Steers clear of {subject}' }, { subject });
    default:
      return null;
  }
};

// A short name for each feature traitSubject names, for the chips on ranked dishes, or null for a feature this version
// of the page has no name for
export const traitLabel = (intl: IntlShape, feature: string) => {
  switch (feature) {
    case 'adventurousness':
      return intl.formatMessage({ defaultMessage: 'Adventurous' });
    case 'breakfast':
      return intl.formatMessage({ defaultMessage: 'Breakfast' });
    case 'cuisine_region.caribbean':
      return intl.formatMessage({ defaultMessage: 'Caribbean' });
    case 'cuisine_region.east_asian':
      return intl.formatMessage({ defaultMessage: 'East Asian' });
    case 'cuisine_region.latin_american':
      return intl.formatMessage({ defaultMessage: 'Latin American' });
    case 'cuisine_region.middle_eastern_north_african':
      return intl.formatMessage({ defaultMessage: 'Middle Eastern & North African' });
    case 'cuisine_region.north_american':
      return intl.formatMessage({ defaultMessage: 'American' });
    case 'cuisine_region.northern_european':
      return intl.formatMessage({ defaultMessage: 'Northern European' });
    case 'cuisine_region.south_asian':
      return intl.formatMessage({ defaultMessage: 'South Asian' });
    case 'cuisine_region.southeast_asian':
      return intl.formatMessage({ defaultMessage: 'Southeast Asian' });
    case 'cuisine_region.southern_european':
      return intl.formatMessage({ defaultMessage: 'Southern European' });
    case 'cuisine_region.sub_saharan_african':
      return intl.formatMessage({ defaultMessage: 'African' });
    case 'dish_form_group.bakery_sweets':
      return intl.formatMessage({ defaultMessage: 'Sweets & baked goods' });
    case 'dish_form_group.handhelds':
      return intl.formatMessage({ defaultMessage: 'Handhelds' });
    case 'dish_form_group.noodles_pasta':
      return intl.formatMessage({ defaultMessage: 'Noodles & pasta' });
    case 'dish_form_group.rice_bowls':
      return intl.formatMessage({ defaultMessage: 'Rice & bowls' });
    case 'dish_form_group.shareables':
      return intl.formatMessage({ defaultMessage: 'Shareables' });
    case 'dish_form_group.soups_curries':
      return intl.formatMessage({ defaultMessage: 'Soups & curries' });
    case 'flavor.bitter':
      return intl.formatMessage({ defaultMessage: 'Bitter' });
    case 'flavor.cheesy':
      return intl.formatMessage({ defaultMessage: 'Cheesy' });
    case 'flavor.funky':
      return intl.formatMessage({ defaultMessage: 'Funky' });
    case 'flavor.garlicky':
      return intl.formatMessage({ defaultMessage: 'Garlicky' });
    case 'flavor.herby':
      return intl.formatMessage({ defaultMessage: 'Herby' });
    case 'flavor.smoky':
      return intl.formatMessage({ defaultMessage: 'Smoky' });
    case 'flavor.sweet':
      return intl.formatMessage({ defaultMessage: 'Sweet' });
    case 'flavor.tangy':
      return intl.formatMessage({ defaultMessage: 'Tangy' });
    case 'flavor.umami':
      return intl.formatMessage({ defaultMessage: 'Savory' });
    case 'flavor.warm_spiced':
      return intl.formatMessage({ defaultMessage: 'Warm spices' });
    case 'heaviness':
      return intl.formatMessage({ defaultMessage: 'Hearty' });
    case 'ingredients.anchovy':
      return intl.formatMessage({ defaultMessage: 'Anchovies' });
    case 'ingredients.avocado':
      return intl.formatMessage({ defaultMessage: 'Avocado' });
    case 'ingredients.blue_cheese':
      return intl.formatMessage({ defaultMessage: 'Blue cheese' });
    case 'ingredients.cilantro':
      return intl.formatMessage({ defaultMessage: 'Cilantro' });
    case 'ingredients.coconut':
      return intl.formatMessage({ defaultMessage: 'Coconut' });
    case 'ingredients.eggplant':
      return intl.formatMessage({ defaultMessage: 'Eggplant' });
    case 'ingredients.mayo':
      return intl.formatMessage({ defaultMessage: 'Mayo & aioli' });
    case 'ingredients.mushroom':
      return intl.formatMessage({ defaultMessage: 'Mushrooms' });
    case 'ingredients.olive':
      return intl.formatMessage({ defaultMessage: 'Olives' });
    case 'ingredients.pickles':
      return intl.formatMessage({ defaultMessage: 'Pickles' });
    case 'ingredients.raw_onion':
      return intl.formatMessage({ defaultMessage: 'Raw onion' });
    case 'method.braised':
      return intl.formatMessage({ defaultMessage: 'Braised' });
    case 'method.fried':
      return intl.formatMessage({ defaultMessage: 'Fried' });
    case 'method.grilled':
      return intl.formatMessage({ defaultMessage: 'Grilled' });
    case 'method.raw':
      return intl.formatMessage({ defaultMessage: 'Raw & cured' });
    case 'method.roasted':
      return intl.formatMessage({ defaultMessage: 'Roasted' });
    case 'method.smoked':
      return intl.formatMessage({ defaultMessage: 'Smoked' });
    case 'method.steamed':
      return intl.formatMessage({ defaultMessage: 'Steamed' });
    case 'method.stir_fried':
      return intl.formatMessage({ defaultMessage: 'Stir-fried' });
    case 'protein.beans_lentils':
      return intl.formatMessage({ defaultMessage: 'Beans & lentils' });
    case 'protein.beef':
      return intl.formatMessage({ defaultMessage: 'Beef' });
    case 'protein.cheese_led':
      return intl.formatMessage({ defaultMessage: 'Cheese-centered' });
    case 'protein.chicken':
      return intl.formatMessage({ defaultMessage: 'Chicken' });
    case 'protein.egg':
      return intl.formatMessage({ defaultMessage: 'Egg' });
    case 'protein.fish':
      return intl.formatMessage({ defaultMessage: 'Fish' });
    case 'protein.game':
      return intl.formatMessage({ defaultMessage: 'Game meats' });
    case 'protein.lamb_goat':
      return intl.formatMessage({ defaultMessage: 'Lamb & goat' });
    case 'protein.other_poultry':
      return intl.formatMessage({ defaultMessage: 'Duck & turkey' });
    case 'protein.pork':
      return intl.formatMessage({ defaultMessage: 'Pork' });
    case 'protein.shellfish':
      return intl.formatMessage({ defaultMessage: 'Shellfish' });
    case 'protein.tofu_tempeh_seitan':
      return intl.formatMessage({ defaultMessage: 'Tofu & plant-based' });
    case 'spice':
      return intl.formatMessage({ defaultMessage: 'Spicy' });
    case 'texture.brothy':
      return intl.formatMessage({ defaultMessage: 'Brothy' });
    case 'texture.chewy':
      return intl.formatMessage({ defaultMessage: 'Chewy' });
    case 'texture.creamy':
      return intl.formatMessage({ defaultMessage: 'Creamy' });
    case 'texture.crispy':
      return intl.formatMessage({ defaultMessage: 'Crispy' });
    case 'texture.saucy':
      return intl.formatMessage({ defaultMessage: 'Saucy' });
    default:
      return null;
  }
};
